import { useEffect, useRef, useCallback } from 'react';

interface UseScrollPositionProps {
  contentRef: React.RefObject<HTMLDivElement | null>;
  bookId: string | null;
  chapterNumber: number;
  enabled: boolean;
  savePosition: (bookId: string, chapter: number, position: number) => Promise<void>;
}

export function useScrollPosition({
  contentRef,
  bookId,
  chapterNumber,
  enabled,
  savePosition,
}: UseScrollPositionProps) {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedPosition = useRef<number>(0);

  const save = useCallback(
    (scrollPercent: number) => {
      if (!bookId || !enabled) return;

      // Only save if position changed by more than 2%
      if (Math.abs(scrollPercent - lastSavedPosition.current) < 2) return;

      lastSavedPosition.current = scrollPercent;

      savePosition(bookId, chapterNumber, scrollPercent).catch((err) => {
        console.error('Failed to save scroll position:', err);
      });
    },
    [bookId, chapterNumber, savePosition, enabled]
  );

  const handleScroll = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) return;

    const scrollPercent = (scrollTop / maxScroll) * 100;

    // Debounce: save after 500ms of no scrolling
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      save(Math.round(scrollPercent));
    }, 500);
  }, [contentRef, save]);

  const restorePosition = useCallback(
    (scrollPercent: number) => {
      const content = contentRef.current;
      if (!content || scrollPercent <= 0) return;

      // Wait for content to render
      requestAnimationFrame(() => {
        const { scrollHeight, clientHeight } = content;
        const maxScroll = scrollHeight - clientHeight;
        if (maxScroll <= 0) return;

        const targetScroll = (scrollPercent / 100) * maxScroll;
        content.scrollTo({ top: targetScroll, behavior: 'smooth' });
      });
    },
    [contentRef]
  );

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !enabled) return;

    content.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      content.removeEventListener('scroll', handleScroll);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [contentRef, handleScroll, enabled]);

  // Reset last saved position when chapter changes
  useEffect(() => {
    lastSavedPosition.current = 0;
  }, [chapterNumber]);

  return { restorePosition };
}
